<template>
  <div v-if="currentUser" class="service-verify-admin">
    <div class="page-header">
      <h1>Weryfikacja usług</h1>
      <div class="header-actions">
        <button class="btn-outline" @click="resetSearch">Resetuj</button>
        <button class="btn-primary" @click="getServices">Odśwież listę</button>
      </div>
    </div>

    <div class="section-card">
      <div class="section-card-header">
        <h2>Wyszukiwanie usług</h2>
        <div class="search-bar">
          <input
              type="text"
              placeholder="Szukaj po nazwie usługi..."
              v-model="searchInput"
              @keyup.enter="searchService"
          >
          <button @click="searchService" class="btn-green">Szukaj</button>
          <button @click="resetSearch" class="btn-outline">Resetuj</button>
        </div>
      </div>

      <div class="toolbar-row">
        <div class="results-chip">
          Wyniki: <b>{{ servicesCount }}</b>
        </div>

        <div class="pagination">
          <button class="btn-outline" @click="prevPage" :disabled="page <= 1">
            &#8592; Poprzednia
          </button>
          <span class="page-info">Strona {{ page }} / {{ totalPages }}</span>
          <button class="btn-outline" @click="nextPage" :disabled="page >= totalPages">
            Następna &#8594;
          </button>
        </div>
      </div>
    </div>

    <div class="section-card services-card">
      <div class="section-card-header">
        <h2>Lista usług</h2>
      </div>

      <ServiceModal />

      <div v-if="isLoading" class="empty-state">
        <p>Ładowanie usług...</p>
      </div>

      <div v-else-if="services && services.length > 0 && states.length > 0 && categories.length > 0" class="services-grid">
        <div class="services-grid__container">
          <ServiceCardVue
            v-for="item in services"
            :key="item.id"
            :admin="true"
            :price="item.price"
            :is-advance="!!item.advanceAmount"
            :img="item.photo_url"
            :id="item.id"
            :name="item.name"
            :state="resolveStateName(item.state_id)"
            :ishighlighted="item.active"
            :category="resolveCategoryName(item.user && item.user.category_id)"
            :userServiceId="item.user && item.user.id"
            :should-show-action-button="false"
            @deleteService="(id, userServiceId) => deleteService(id, userServiceId)"
            @verify="() => verifyService(item.id)"
            @openServiceModal="() => $router.push({ path: '/admin-profile/service-verify', query: { serviceID: item.id } })"
          />
        </div>
      </div>

      <div v-else class="empty-state">
        <p>Nie znaleziono usług.</p>
      </div>
    </div>
  </div>
</template>

<script>
import ServiceCardVue from '@/components/SpecialComponents/ServiceCard.vue';
import ServiceModal from '@/components/SpecialComponents/ServiceModal.vue';
import authHeader from '@/services/auth-header';
import axios from 'axios';
import { useAuthStore } from '@/stores/auth';
import { useAppStore } from '@/stores/app';

export default {
  name: 'ServiceVerifying',
  components: { ServiceCardVue, ServiceModal },
  setup() {
    return {
      authStore: useAuthStore(),
      appStore: useAppStore(),
    };
  },
  data() {
    return {
      services: null,
      servicePopup: {
        visibility: false,
        serviceId: 0,
      },
      searchInput: '',
      states: [],
      categories: [],
      page: 1,
      servicesCount: 0,
      isLoading: false,
    };
  },
  props: ['isVisible'],
  computed: {
    currentUser() {
      return this.authStore.user;
    },
    totalPages() {
      const pages = Math.ceil(this.servicesCount / 100);
      return pages > 0 ? pages : 1;
    },
  },
  mounted() {
    if (!this.currentUser) {
      this.$router.push('/login');
    } else if (this.currentUser.role_id !== 1) {
      this.$router.push('/login');
    }
    this.getStates();
    this.getCategories();
    this.getServices();
  },
  methods: {
    getServices() {
      this.isLoading = true;
      axios.post('/Services', { page: this.page, limit: 100, name: this.searchInput || undefined })
          .then(res => {
            this.services = res.data.items || [];
            this.servicesCount = res.data.count || 0;
          })
          .catch(() => {
            this.services = [];
            this.servicesCount = 0;
            this.appStore.setIsAlert('Nie udało się pobrać usług');
          })
          .finally(() => {
            this.isLoading = false;
          });
    },
    closePopup(value) {
      if (value) {
        this.servicePopup.visibility = false;
      }
    },
    showService(value) {
      if (value) {
        this.servicePopup.serviceId = value[1];
        this.servicePopup.visibility = value[0];
      }
    },
    verifyService(value) {
      axios.get(`/ServiceEnableByAdmin/${value}`, authHeader())
          .then(res => {
            if (String(res.status).charAt(0) === '2') {
              this.appStore.setIsSuccess('Usługa została zweryfikowana');
              this.getServices();
            }
          })
          .catch(() => {
            this.appStore.setIsAlert('Nie udało się zweryfikować usługi');
          });
    },
    searchService() {
      this.page = 1;
      this.getServices();
    },
    resetSearch() {
      this.searchInput = '';
      this.page = 1;
      this.getServices();
    },
    prevPage() {
      if (this.page > 1) {
        this.page -= 1;
        this.getServices();
      }
    },
    nextPage() {
      if (this.page < this.totalPages) {
        this.page += 1;
        this.getServices();
      }
    },
    getStates() {
      axios.get('/State')
          .then(res => {
            this.states = (res.data || []).map(item => ({
              name: item.name,
              value: String(item.id),
            }));
          })
          .catch(() => {
            this.states = [];
          });
    },
    getCategories() {
      axios.get('/Categories')
          .then(res => {
            this.categories = (res.data || []).map(item => ({
              name: item.name,
              id: String(item.id),
            }));
          })
          .catch(() => {
            this.categories = [];
          });
    },
    resolveStateName(stateId) {
      const found = this.states.find(state => state.value === String(stateId));
      return found ? found.name : '—';
    },
    resolveCategoryName(categoryId) {
      const found = this.categories.find(category => category.id === String(categoryId));
      return found ? found.name : '—';
    },
    deleteService(id, userServiceId) {
      axios.get(`/UserService/${userServiceId}`)
          .then(res => {
            axios.delete(`/Service/${id}`, authHeader()).then(() => {
              const userService = (res.data || []).find(serv => serv.service && serv.service.id == id);
              if (!userService) {
                this.appStore.setIsAlert('Nie znaleziono powiązanej usługi użytkownika');
                return;
              }

              axios.delete(`/UserService/${userService.id}`, authHeader()).then(() => {
                this.appStore.setIsSuccess('Pomyślnie usunięto usługę');
                this.getServices();
              });
            }).catch(() => {
              this.appStore.setIsAlert('Nie udało się usunąć usługi');
            });
          })
          .catch(() => {
            this.appStore.setIsAlert('Nie udało się pobrać danych usługi użytkownika');
          });
    },
  },
};
</script>

<style scoped>
.service-verify-admin {
  max-width: 1200px;
  margin: 0 auto;
  padding: 32px 16px;
  font-family: sans-serif;
  box-sizing: border-box;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 2px solid #e5e7eb;
}

.page-header h1 {
  font-size: 2.8rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.section-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.08);
  padding: 24px;
  box-sizing: border-box;
  width: 100%;
  margin-bottom: 20px;
}

.section-card-header {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 14px;
}

.section-card h2 {
  font-size: 2rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
}

.search-bar {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.search-bar input {
  flex: 1 1 240px;
  min-width: 0;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 8px 14px;
  font-size: 1.5rem;
  outline: none;
  transition: border-color 0.2s;
  box-sizing: border-box;
}

.search-bar input:focus {
  border-color: #10b981;
}

.toolbar-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding-top: 8px;
  border-top: 1px solid #e5e7eb;
}

.results-chip {
  font-size: 1.4rem;
  color: #374151;
  background: #f3f4f6;
  border-radius: 999px;
  padding: 6px 12px;
}

.services-card {
  margin-bottom: 0;
}

.services-grid {
  width: 100%;
  margin-top: 6px;
  display: flex;
  justify-content: space-evenly;
  flex-wrap: wrap;
  gap: 22px 10px;
}

.services-grid__container {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 18px;
}

.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.page-info {
  font-size: 1.5rem;
  color: #6b7280;
  font-weight: 500;
  min-width: 130px;
  text-align: center;
}

.empty-state {
  text-align: center;
  padding: 28px;
  color: #9ca3af;
  font-size: 1.5rem;
  border: 1px dashed #d1d5db;
  border-radius: 10px;
}

.btn-primary {
  background-color: #f97316;
  color: #fff;
  padding: 10px 20px;
  border-radius: 8px;
  border: none;
  font-size: 1.5rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-primary:hover {
  background-color: #ea6c0a;
}

.btn-green {
  background-color: #10b981;
  color: #fff;
  padding: 8px 16px;
  border-radius: 8px;
  border: none;
  font-size: 1.5rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-green:hover {
  background-color: #059669;
}

.btn-outline {
  background-color: transparent;
  color: #1f2937;
  padding: 8px 16px;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  font-size: 1.5rem;
  cursor: pointer;
  transition: border-color 0.2s, color 0.2s;
}

.btn-outline:hover {
  border-color: #10b981;
  color: #10b981;
}

button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .toolbar-row {
    flex-direction: column;
    align-items: stretch;
  }

  .pagination {
    justify-content: space-between;
  }
}

@media (max-width: 480px) {
  .service-verify-admin {
    padding: 16px 12px;
  }

  .page-header h1 {
    font-size: 2.2rem;
  }

  .section-card {
    padding: 16px 12px;
    border-radius: 8px;
  }

  .section-card h2 {
    font-size: 1.8rem;
  }

  .search-bar {
    flex-direction: column;
  }

  .search-bar input,
  .btn-primary,
  .btn-green,
  .btn-outline {
    width: 100%;
  }

  .header-actions {
    width: 100%;
  }
}
</style>